import { Component } from '@theme/component';

const sources = new Map();
const surfaces = new Set();
let clockController;
let clockTimer;
let boundaryAt;
let boundaryDate;
let dateCore;

export function assetURL(value, filename) {
  const url = new URL(value, location.href);
  if (url.protocol !== 'https:' || url.username || url.password || ![location.hostname, 'cdn.shopify.com'].includes(url.hostname) || !url.pathname.endsWith(`/${filename}`)) throw new Error('Invalid annual asset');
  return url.href;
}

export async function fetchCalendar(url, core, controller) {
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(url, {signal:controller.signal, credentials:'omit'});
    if (!response.ok || controller.signal.aborted) return null;
    const size = Number(response.headers.get('content-length'));
    if (Number.isFinite(size) && size > core.MAX_ASSET_BYTES) return null;
    let raw;
    if (response.body?.getReader) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8', {fatal:true});
      let total = 0;
      raw = '';
      try {
        for (;;) {
          const {done,value} = await reader.read();
          if (done) break;
          total += value.byteLength;
          if (total > core.MAX_ASSET_BYTES || controller.signal.aborted) { await reader.cancel(); return null; }
          raw += decoder.decode(value,{stream:true});
        }
        raw += decoder.decode();
      } finally { reader.releaseLock(); }
    } else raw = await response.text();
    return controller.signal.aborted ? null : core.parseCalendar(raw);
  } catch { return null; }
  finally { clearTimeout(timeout); }
}

function acquireSource(value, coreURL) {
  const url = assetURL(value,'his-music-wire-editorial.json');
  const moduleURL = assetURL(coreURL,'his-on-this-day-annual-core.js');
  const key = `${url}|${moduleURL}`;
  let source = sources.get(key);
  if (!source || (source.failedAt && Date.now() - source.failedAt >= 30000)) {
    source = {users:0,controller:new AbortController(),promise:null,failedAt:0};
    source.promise = (async () => {
      try {
        const core = await import(moduleURL);
        if (source.controller.signal.aborted) return null;
        dateCore = core;
        const calendar = await fetchCalendar(url,core,source.controller);
        if (!calendar) { source.failedAt = Date.now(); return null; }
        return {core,calendar};
      } catch { source.failedAt = Date.now(); return null; }
    })();
    sources.set(key,source);
  }
  source.users += 1;
  let released = false;
  return {promise:source.promise, source, release() {
    if (released) return;
    released = true;
    source.users -= 1;
    if (!source.users) { source.controller.abort(); if (sources.get(key) === source) sources.delete(key); }
  }};
}

function refreshSurfaces(event) {
  clearTimeout(clockTimer);
  const now = new Date();
  for (const surface of surfaces) surface.refresh(now);
  if (event && !document.hidden) for (const surface of surfaces) surface.retry();
  if (!surfaces.size || document.hidden) return;
  let wait = Math.max(20,60000 - (now.getTime() % 60000));
  const date = dateCore?.denverDay(now);
  const key = date ? `${date.year}-${date.key}` : '';
  if (key !== boundaryDate || !boundaryAt || boundaryAt <= now.getTime()) {
    boundaryDate = key;
    boundaryAt = dateCore?.nextDenverBoundary(now);
  }
  if (boundaryAt > now.getTime()) wait = Math.min(wait,boundaryAt - now.getTime());
  clockTimer = setTimeout(refreshSurfaces,wait);
}

function subscribe(surface) {
  surfaces.add(surface);
  if (!clockController) {
    clockController = new AbortController();
    const signal = clockController.signal;
    document.addEventListener('visibilitychange',refreshSurfaces,{signal});
    window.addEventListener('pageshow',refreshSurfaces,{signal});
    window.addEventListener('focus',refreshSurfaces,{signal});
  }
  refreshSurfaces();
  return () => {
    surfaces.delete(surface);
    if (!surfaces.size) { clearTimeout(clockTimer); clockController.abort(); clockController = undefined; boundaryAt = undefined; boundaryDate = undefined; }
  };
}

const make = (tag,className,text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

export function recordHistoryURL(entry) {
  const url = new URL(entry.related_record_url);
  if (url.protocol !== 'https:' || url.hostname !== 'nadeltonrecordings.com'
      || url.username || url.password || url.search || url.hash
      || !/^\/products\/[a-z0-9][a-z0-9-]*$/.test(url.pathname)
      || !/^[A-Za-z0-9_-]{1,100}$/.test(entry.editorial_id)) throw new Error('Invalid record history link');
  url.hash = `music-wire-${entry.editorial_id.toLowerCase()}`;
  return url.href;
}

export function storyURL(root) {
  const url = new URL(root,location.href);
  if (url.origin !== location.origin || url.username || url.password) throw new Error('Invalid local root');
  if (!url.pathname.endsWith('/')) url.pathname += '/';
  url.search = '';
  const current = new URL(location.href);
  const theme = current.searchParams.get('preview_theme_id');
  if (theme && /^\d+$/.test(theme)) url.searchParams.set('preview_theme_id',theme);
  url.hash = 'on-this-day';
  return url.pathname + url.search + url.hash;
}

class HisAnnualOnThisDay extends Component {
  #unsubscribe;
  #lease;
  #epoch = 0;
  #ready;
  #renderKey = '';
  #hasRevealed = false;
  #failedAt = 0;

  connectedCallback() { super.connectedCallback(); this.#start(); }
  updatedCallback() { super.updatedCallback(); this.#start(); }
  disconnectedCallback() { this.#stop(); super.disconnectedCallback(); }

  #hide() {
    this.hidden = true;
    this.#renderKey = '';
    delete this.dataset.selectedId;
    delete this.dataset.localDate;
    this.querySelector('[data-annual-content]')?.replaceChildren();
  }

  #stop() {
    this.#epoch += 1;
    this.#unsubscribe?.();
    this.#unsubscribe = undefined;
    this.#lease?.release();
    this.#lease = undefined;
    this.#ready = undefined;
    this.#hide();
  }

  async #start() {
    this.#stop();
    this.#failedAt = 0;
    const epoch = this.#epoch;
    this.#unsubscribe = subscribe(this);
    try {
      this.#lease = acquireSource(this.dataset.source,this.dataset.core);
      const ready = await this.#lease.promise;
      if (epoch !== this.#epoch || !this.isConnected) return;
      this.#ready = ready;
      if (!ready) this.#failedAt = Date.now();
      refreshSurfaces();
    } catch { if (epoch === this.#epoch) { this.#failedAt = Date.now(); this.#hide(); } }
  }

  retry() {
    if (this.isConnected && this.#failedAt && Date.now() - this.#failedAt >= 30000) {
      this.#lease?.release();
      this.#lease = undefined;
      this.#start();
    }
  }

  refresh(now) {
    if (!this.isConnected || document.hidden || !this.#ready) { this.#hide(); return; }
    const {core,calendar} = this.#ready;
    const preview = core.editorInstant(this.dataset.previewDate,this.dataset.designMode,window.Shopify?.designMode);
    const invalidPreview = this.dataset.designMode === 'true' && window.Shopify?.designMode === true && this.dataset.previewDate && !preview;
    const selected = invalidPreview ? null : core.chooseAnnualEntry(calendar,preview || now);
    const content = this.querySelector('[data-annual-content]');
    if (!selected || !content) { this.#hide(); return; }
    const key = `${selected.localDate}:${selected.entry.id}:${Boolean(preview)}`;
    try {
      if (key !== this.#renderKey) { this.#hide(); this.#render(content,selected.entry,Boolean(preview)); this.#renderKey = key; }
      this.dataset.selectedId = selected.entry.id;
      this.dataset.localDate = selected.localDate;
      this.hidden = false;
      if (!this.#hasRevealed && this.id === 'on-this-day' && location.hash === '#on-this-day') this.scrollIntoView({block:'start'});
      this.#hasRevealed = true;
    } catch { this.#hide(); }
  }

  #render(content,entry,preview) {
    const sidebar = this.dataset.presentation === 'sidebar';
    if (this.dataset.presentation === 'announcement') {
      const bar = make('div','his-anniversary__announcement');
      if (preview) bar.append(make('p','his-anniversary__announcement-preview',this.dataset.previewLabel));
      const link = make('a','his-anniversary__announcement-link');
      link.href = recordHistoryURL(entry);
      link.setAttribute('aria-label',`${this.dataset.readLabel}: ${entry.title}`);
      link.append(make('span','his-anniversary__announcement-label',this.dataset.heading),make('span','his-anniversary__announcement-title',entry.title));
      bar.append(link); content.replaceChildren(bar); return;
    }
    const fragment = document.createDocumentFragment();
    if (sidebar) {
      const header = make('header','his-anniversary-sidebar__header');
      header.append(make('h3','his-anniversary-sidebar__heading',this.dataset.sidebarHeading),make('p','his-anniversary-sidebar__explainer',this.dataset.explainer));
      fragment.append(header);
    }
    const card = make('article','his-anniversary__card');
    card.lang = 'en';
    const dateColumn = make('div','his-anniversary__date-column');
    if (!sidebar) dateColumn.append(make('p','his-anniversary__label',this.dataset.heading));
    const body = make('div','his-anniversary__body');
    if (preview) body.append(make('p','his-anniversary__preview',this.dataset.previewLabel));
    body.append(make(sidebar?'h4':'h2','his-anniversary__title',entry.title),make('p','his-anniversary__story',entry.body));
    const recordLine = entry.connection_scope === 'record' ? entry.record : `${entry.record} · Track ${entry.track_number}: ${entry.track}`;
    body.append(make('p','his-anniversary__record',recordLine));
    const action = make('a','np-notice-link his-anniversary__record-link');
    action.href = recordHistoryURL(entry);
    const arrow = make('span','his-anniversary__action-arrow','→');
    arrow.setAttribute('aria-hidden','true');
    action.append(make('span','',this.dataset.readLabel),arrow);
    body.append(action); if (!sidebar) card.append(dateColumn); card.append(body); fragment.append(card); content.replaceChildren(fragment);
  }
}

if (!customElements.get('his-annual-on-this-day')) customElements.define('his-annual-on-this-day',HisAnnualOnThisDay);

